import React, { useEffect, useState, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import VMFLogo from '../../../VMFLogo/VMFLogo';
import ButtonLink from '../../../Shared/ButtonLink';
import MyAccountIcon from '../../../Shared/Icons/MyAccountIcon';
import Link from '../../../Shared/Link';
import UpdatedDesktopHeaderStyles from './UpdatedDesktopHeader.styled';
import {
  SingleLinkProps,
  MultiLinkProps,
  defaultNavItems,
  defaultNavSection,
  defaultSignInLinks,
  defaultButtonLinks,
} from '../defaultNavData';
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside';

/**
 * Props for the `UpdatedDesktopHeader` component.
 */
interface DesktopHeaderProps {
  /** Links for the top navigation section. */
  navSection?: SingleLinkProps[];
  /** Links for the main navigation items. */
  navItems?: MultiLinkProps[];
  /** Links for the sign-in dropdown. */
  signInLinks?: MultiLinkProps[];
  /** Links for the buttons in the header. */
  buttonLinks?: SingleLinkProps[];
}

/**
 * The `UpdatedDesktopHeader` component renders the desktop header with navigation links, sign-in options, and buttons.
 *
 * @param {DesktopHeaderProps} props - The props for the component.
 * @returns {JSX.Element} The rendered desktop header component.
 */
const UpdatedDesktopHeader: React.FC<DesktopHeaderProps> = ({
  navSection = defaultNavSection,
  navItems = defaultNavItems,
  signInLinks = defaultSignInLinks,
  buttonLinks = defaultButtonLinks,
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  /**
   * Toggles the dropdown for a given navigation item.
   *
   * @param {string} index - The index of the navigation item to toggle.
   */
  const toggleDropdown = (index: string) => {
    if (activeDropdown === index) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(index);
    }
  };

  /**
   * Handles key press events for toggling dropdowns.
   *
   * @param {React.KeyboardEvent} event - The keyboard event.
   * @param {string} index - The index of the navigation item to toggle.
   */
  const handleKeyPress = (event: React.KeyboardEvent, index: string) => {
    if (event.key === 'Enter') {
      if (activeDropdown === index) {
        setActiveDropdown(null);
      } else {
        setActiveDropdown(index);
      }
    }
  };

  useEffect(() => {
    /**
     * Closes the dropdown when the user scrolls.
     */
    const handleScroll = () => {
      setActiveDropdown(null);
    };

    document.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navContainerRef = useRef<HTMLDivElement>(null);
  const signInRef = useRef<HTMLDivElement>(null);

  const handleOutsideClicks = (e: MouseEvent) => {
    if (activeDropdown) {
      const navArea = navContainerRef.current;
      const signInArea = signInRef.current;

      // Check if click was outside both areas
      const clickedOutsideNav =
        !navArea ||
        (e.target !== navArea && !navArea.contains(e.target as Node));
      const clickedOutsideSignIn =
        !signInArea ||
        (e.target !== signInArea && !signInArea.contains(e.target as Node));

      // If click was outside both areas, close the dropdown
      if (clickedOutsideNav && clickedOutsideSignIn) {
        setActiveDropdown(null);
      }
    }
  };

  if (typeof document !== 'undefined') {
    useOnClickOutside([navContainerRef, signInRef], handleOutsideClicks);
  }

  return (
    <UpdatedDesktopHeaderStyles>
      {navSection && (
        <div className='top-section'>
          {navSection.map((item, index) => (
            <Link
              to={item.to}
              includeArrowIcon={false}
              className={`nav-section-links ${item.gtmClassname ? item.gtmClassname : ''}`}
              key={`nav-section-link-dt-${index}`} // Add a unique key
            >
              {item.title}
            </Link>
          ))}
        </div>
      )}
      <div className='bottom-section'>
        <div className='logo'>
          <NavLink to='/'>
            <VMFLogo />
          </NavLink>
        </div>
        <div className='nav-container'>
          {/* Navigation items */}
          {navItems && (
            <div className='nav-left' ref={navContainerRef}>
              {navItems.map((item, index) =>
                item.links.length > 1 ? (
                  <div
                    key={`nav-item-${index}`} // Add a unique key
                    className='nav-item'
                    onClick={() => toggleDropdown(item.title)}
                    onKeyDown={e => {
                      handleKeyPress(e, item.title);
                    }}
                    tabIndex={0}
                  >
                    {item.title}
                    <div
                      className={`dropdown ${activeDropdown === item.title ? 'active' : ''}`}
                    >
                      {item.links.map((link, linkIndex) => (
                        <NavLink
                          key={`dropdown-link-${index}-${linkIndex}`} // Add a unique key
                          to={link.to}
                          className='dropdown-link'
                        >
                          {link.title}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={`nav-item-${index}`} // Add a unique key
                    className='nav-item'
                    to={item?.links[0].to}
                  >
                    {item.links[0].title || item.title}
                  </NavLink>
                )
              )}
            </div>
          )}
          <div className='nav-right'>
            {/* Sign-in dropdown */}
            {signInLinks && (
              <div className='sign-in' ref={signInRef}>
                {signInLinks.map((item, index) => (
                  <div
                    key={`sign-in-item-${index}`} // Add a unique key
                    className={`sign-in-item ${item.gtmClassname ? item.gtmClassname : ''}`}
                    onClick={() => toggleDropdown(item.title)}
                    tabIndex={0}
                    onKeyDown={e => {
                      handleKeyPress(e, item.title);
                    }}
                  >
                    {item.title}
                    <MyAccountIcon />
                    <div
                      className={`sign-in-dropdown ${activeDropdown === item.title ? 'active' : ''}`}
                    >
                      {item.links.map((link, linkIndex) => (
                        <Link
                          key={`sign-in-dropdown-link-${index}-${linkIndex}`} // Add a unique key
                          to={link.to}
                          className={`sign-in-dropdown-link ${link.gtmClassname ? link.gtmClassname : ''}`}
                          includeArrowIcon={false}
                        >
                          {link.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Button links */}
            {buttonLinks && (
              <div className='button-links'>
                {buttonLinks.map((item, index) => (
                  <ButtonLink
                    key={`button-link-${index}`} // Add a unique key
                    to={item.to}
                    appearance='dark'
                    hoverAppearance='dark'
                    size='small'
                    isExternal={true}
                    newTab={true}
                    className={`apply-button ${item.gtmClassname ? item.gtmClassname : ''}`}
                  >
                    {item.title}
                  </ButtonLink>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </UpdatedDesktopHeaderStyles>
  );
};

export default UpdatedDesktopHeader;
